'use client';

import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/useAuth';
import { PORTFOLIO_QUERY_KEYS } from '@/services/query';

import { MESSAGES } from '@/constants/messages.constants';
import {
  NATIVE_USDC_CONTRACT_ADDRESS,
  PUSD_CONTRACT_ADDRESS,
  USDC_E_CONTRACT_ADDRESS,
} from '@/constants/tokens.constants';

const padAddress = (addr: string) => {
  const clean = addr.toLowerCase().replace(/^0x/, '');
  return '0x' + '0'.repeat(24) + clean;
};

export function WalletActivityWatcher() {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      // console.log('[WalletActivityWatcher] No active authenticated user session found, waiting for login...');
      return;
    }

    const addressesToWatch: string[] = [];
    if (user.walletAddress) addressesToWatch.push(user.walletAddress);
    if (user.proxyWalletAddress) addressesToWatch.push(user.proxyWalletAddress);

    if (addressesToWatch.length === 0) {
      return;
    }

    const apiKey = process.env.NEXT_PUBLIC_ALCHEMY_BEARER_TOKEN || 'T7bAMb2Gq3a6hknoxsEpo';

    const urls = [
      `wss://polygon-mainnet.g.alchemy.com/v2/${apiKey}`,
      'wss://polygon.drpc.org',
      'wss://polygon-bor-rpc.publicnode.com',
    ];

    let urlIndex = 0;
    let isClosed = false;
    let reconnectTimeout: any;
    let keepAliveInterval: any;

    function connect() {
      if (isClosed) return;

      const wsUrl = urls[urlIndex % urls.length];

      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;

      ws.onopen = () => {
        // Subscribe to ERC20 Transfer logs targeting our watch addresses (EOA + Proxy)
        const paddedAddresses = addressesToWatch.map(padAddress);
        const subscriptionPayload = {
          jsonrpc: '2.0',
          id: 1,
          method: 'eth_subscribe',
          params: [
            'logs',
            {
              address: [
                NATIVE_USDC_CONTRACT_ADDRESS,
                USDC_E_CONTRACT_ADDRESS,
                PUSD_CONTRACT_ADDRESS,
              ],
              topics: [
                '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef', // Transfer signature
                null, // from (any)
                paddedAddresses, // to (our EOA or proxy address)
              ],
            },
          ],
        };

        ws.send(JSON.stringify(subscriptionPayload));

        // Keepalive ping every 30 seconds to prevent connection drops
        keepAliveInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ jsonrpc: '2.0', id: 2, method: 'net_version', params: [] }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.method === 'eth_subscription' && message.params?.result) {
            const log = message.params.result;
            const tokenAddress = log.address?.toLowerCase();
            const dataHex = log.data;
            const amount = Number(BigInt(dataHex)) / 1000000;

            let tokenName = 'USDC';
            if (tokenAddress === PUSD_CONTRACT_ADDRESS.toLowerCase()) {
              tokenName = 'pUSD';
            } else if (tokenAddress === USDC_E_CONTRACT_ADDRESS.toLowerCase()) {
              tokenName = 'USDC.e';
            }

            // Ignore zero-value transfer logs (emitted during approvals, payouts, or contract events)
            if (amount <= 0) {
              queryClient.invalidateQueries({ queryKey: PORTFOLIO_QUERY_KEYS.all });
              return;
            }

            // Display a beautiful real-time toast
            toast.success(MESSAGES.TOAST.INCOMING_TRANSACTION(amount.toFixed(2), tokenName), {
              position: 'top-right',
              autoClose: 5000,
            });

            // Invalidate TanStack query cache so balances and components instantly refresh
            queryClient.invalidateQueries({ queryKey: PORTFOLIO_QUERY_KEYS.all });
            setTimeout(() => {
              queryClient.refetchQueries({ queryKey: PORTFOLIO_QUERY_KEYS.all });
            }, 1000);
          }
        } catch (err) {
          console.error('[WalletActivityWatcher] Error parsing ws log:', err);
        }
      };

      ws.onerror = () => {
        // Silently ignore — onerror always fires before onclose when a connection fails.
        // The reconnect/failover logic is handled in ws.onclose below.
      };

      ws.onclose = () => {
        clearInterval(keepAliveInterval);
        console.log(
          `[WalletActivityWatcher] WebSocket closed on ${wsUrl}. Reconnecting to fallback RPC...`
        );
        urlIndex++; // Failover: trigger connection increment to test the next RPC in the array
        if (!isClosed) {
          reconnectTimeout = setTimeout(connect, 3000);
        }
      };
    }

    connect();

    return () => {
      isClosed = true;
      clearInterval(keepAliveInterval);
      clearTimeout(reconnectTimeout);
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [user, queryClient, isAuthenticated]);

  return null;
}
